import React, { useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useNetwork } from 'wagmi'
import dayjs, { unix } from 'dayjs'
import { useBoolean } from 'ahooks'
import PageLayout from '@/components/PageLayout'
import { RowBetweenCenter, RowCenter, RowCenterY } from '@/components/common/Rows'
import Banner from '@/components/farm/Banner'
import { Tab2, Tabs2 } from '@/components/common/Tabs2'
import Switch from '@/components/common/Switch'
import PoolCard from '@/components/farm/PoolCard'
import { usePoolInfo, usePoolsCount, useUserStakedPoolId } from '@/hooks/farmHooks'
import { useWallet } from '@/components/common/WalletContext'
import { CHAIN_SUPPORT_FARM_ADDRESS } from '@/constants/contract/contractAddress'

export type PoolStateFilter = 'live' | 'finished'

const Content = () => {
  const [poolStateFilter, setPoolStateFilter] = useState<PoolStateFilter>('live')
  const [isStakedOnly, { toggle: toggleStakedOnly }] = useBoolean(false)

  const { data: poolsCount } = usePoolsCount()
  const poolIds = poolsCount ? new Array(poolsCount.toNumber()).fill('').map((_, index) => index) : undefined
  const {
    data: poolsInfo,
    refresh: refreshPoolsInfo,
    loading: isPoolsInfoLoading,
  } = usePoolInfo(poolIds, { pollingInterval: poolStateFilter === 'live' ? 30000 : undefined })
  const {
    data: stakedPoolIds,
    loading: isLoadingStakedPoolIds,
    refresh: refreshStakedPoolIds,
  } = useUserStakedPoolId(poolIds)

  return (
    <>
      <RowBetweenCenter sx={{ mt: { xs: 64, md: 20 } }}>
        <Tabs2>
          <Tab2
            width={'80px'}
            isActive={poolStateFilter === 'live'}
            onClick={() => {
              setPoolStateFilter('live')
            }}
          >
            Live
          </Tab2>
          <Tab2
            width={'80px'}
            isActive={poolStateFilter === 'finished'}
            onClick={() => {
              setPoolStateFilter('finished')
            }}
          >
            Finished
          </Tab2>
        </Tabs2>

        <RowCenterY>
          <Typography>Staked Only</Typography>
          <Switch
            disabled={isLoadingStakedPoolIds || !stakedPoolIds}
            checked={isStakedOnly}
            onChange={() => {
              toggleStakedOnly()
            }}
          />
        </RowCenterY>
      </RowBetweenCenter>

      {!poolsInfo ? (
        <RowCenter sx={{ height: 500 }}>
          <CircularProgress />
        </RowCenter>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'auto',
              sm: 'repeat(2, minmax(300px, 1fr) )',
              md: 'repeat(3, minmax(300px, 1fr) )',
            },
            gap: 24,
            mt: 24,
          }}
        >
          {poolsInfo
            ?.filter((pool) =>
              poolStateFilter === 'finished'
                ? unix(pool.closeAt.toNumber()).isBefore(dayjs())
                : !unix(pool.closeAt.toNumber()).isBefore(dayjs()),
            )
            .filter((pool) => (isStakedOnly && stakedPoolIds ? stakedPoolIds.includes(pool.id) : true))
            .map((info) => (
              <PoolCard
                key={info.id}
                stakeTokenAddress={info.stakeToken}
                rewardTokenAddress={info.rewardToken}
                startAt={info.openAt.toNumber()}
                endAt={info.closeAt.toNumber()}
                rewardPerSecond={info.rewardPerSecond}
                totalStakedAmount={info.totalStakedAmount}
                poolId={info.id}
                lockTime={info.lockTime.toNumber()}
                onChange={() => {
                  refreshPoolsInfo()
                  refreshStakedPoolIds()
                }}
                pollAtInterval={poolStateFilter === 'live'}
                loading={isPoolsInfoLoading}
              />
            ))}
        </Box>
      )}
    </>
  )
}

const FarmPage = () => {
  const { walletType, isConnected } = useWallet()
  const { chain } = useNetwork()

  return (
    <PageLayout>
      <Box maxWidth={{ xs: '100%', md: 1228 }} margin={'0 auto'} px={{ xs: 24, md: 32 }} py={32}>
        <Banner />

        {isConnected && walletType === 'Evm' ? (
          CHAIN_SUPPORT_FARM_ADDRESS.includes(chain?.id) ? (
            <Content />
          ) : (
            <RowCenter sx={{ height: 500 }}>
              <Typography fontSize={32} lineHeight={1.5} textAlign={{ xs: 'center', md: 'unset' }}>
                No pool on current chain
              </Typography>
            </RowCenter>
          )
        ) : (
          <RowCenter sx={{ height: 500 }}>
            <Typography fontSize={32} lineHeight={1.5} textAlign={{ xs: 'center', md: 'unset' }}>
              Please connect your EVM wallet
            </Typography>
          </RowCenter>
        )}
      </Box>
    </PageLayout>
  )
}

export default FarmPage

export async function getServerSideProps({ req, res }) {
  res.setHeader('Cache-Control', 'public, s-maxage=3600, stale-while-revalidate=300')

  return {
    props: {},
  }
}
